import { useSettingsSearch } from "../context/SettingsSearchContext";
import settingsTabAsset0 from "../assets/SystemSettings/setting.svg";
import settingsTabAsset1 from "../assets/SystemSettings/video.svg";
import settingsTabAsset2 from "../assets/SystemSettings/emil.svg";
import settingsTabAsset3 from "../assets/SystemSettings/prompt.svg";
import settingsTabAsset4 from "../assets/SystemSettings/code.svg";
import settingsTabAsset5 from "../assets/SystemSettings/Predefined Questions.svg";
import settingsTabAsset6 from "../assets/SystemSettings/Video Providers & TTS.svg";
import settingsTabAsset7 from "../assets/SystemSettings/avatar.svg";
import settingsTabAsset8 from "../assets/SystemSettings/Appearance.svg";
import "./Settings.css";
import React, { useMemo, useState } from "react";
import {
  Settings as SettingsIcon,
  Bot,
  Mail,
  Clock,
  Image as ImageIcon,
  Lock,
  Sparkles,
  Volume2,
  Palette,
  Code,
} from "lucide-react";
import { useRBAC } from "../context/RBACContext";
import { RESOURCE_DISPLAY_NAMES, ACTION_NAMES } from "../utils/resourceMapping";
import EmailSettings from "./sections/EmailSettings";
import InterviewSettings from "./sections/InterviewSettings";
import LLMSettings from "./sections/LLMSettings";
import AIPromptsSettings from "./sections/AIPromptsSettings";
import VoiceProviderSettings from "./sections/VoiceProviderSettings";
import AvatarSettings from "./sections/AvatarSettings";
import ThemeSettings from "./sections/ThemeSettings";
import CodingQuestionsSettings from "./sections/CodingQuestionsSettings";
import PredefinedQuestions from "./sections/PredefinedQuestions";

const AccessDeniedState = () => (
  <div className="flex items-center justify-center min-h-96 flex-col gap-4 px-6">
    <div className="bg-red-100 p-4 rounded-xl">
      <Lock className="w-10 h-10 text-red-600" />
    </div>
    <h2 className="text-2xl font-bold text-gray-900">Access Denied</h2>
    <p className="text-gray-600 text-center max-w-md font-medium">
      You don't have permission to access System Settings. Contact your
      administrator if you believe this is an error.
    </p>
  </div>
);

const Settings = () => {
  const { hasResourcePermissionByName } = useRBAC();
  const [activeTab, setActiveTab] = useState("llm");
  const { tabSearch, setTabSearch } = useSettingsSearch();

  const canViewSettings = hasResourcePermissionByName(
    RESOURCE_DISPLAY_NAMES.SYSTEM_SETTINGS,
    ACTION_NAMES.GET_DATA,
  );
  const canEditSettings = hasResourcePermissionByName(
    RESOURCE_DISPLAY_NAMES.SYSTEM_SETTINGS,
    ACTION_NAMES.EDIT_ITEM,
  );
  const canDeleteSettings = hasResourcePermissionByName(
    RESOURCE_DISPLAY_NAMES.SYSTEM_SETTINGS,
    ACTION_NAMES.DELETE_BUTTON,
  );

  const tabs = useMemo(
    () => [
      { asset: settingsTabAsset0, id: "llm", label: "LLM Settings", icon: Bot },
      { asset: settingsTabAsset1, id: "interview", label: "Interview", icon: Clock },
      { asset: settingsTabAsset2, id: "email", label: "Email", icon: Mail },
      { asset: settingsTabAsset3, id: "prompts", label: "AI Prompts", icon: Sparkles },
      { asset: settingsTabAsset4, id: "coding", label: "Coding Questions", icon: Code },
      { asset: settingsTabAsset5, id: "predefined", label: "Predefined Questions", icon: Code },
      { asset: settingsTabAsset6, id: "tts", label: "Voice Providers & TTS", icon: Volume2 },
      { asset: settingsTabAsset7, id: "avatar", label: "Avatar", icon: ImageIcon },
      { asset: settingsTabAsset8, id: "theme", label: "Appearance", icon: Palette },
    ],
    [],
  );

  const tabContent = {
    theme: <ThemeSettings />,
    llm: (
      <LLMSettings
        canEditSettings={canEditSettings}
        canViewSettings={canViewSettings}
        canDeleteSettings={canDeleteSettings}
      />
    ),
    interview: (
      <InterviewSettings
        canEditSettings={canEditSettings}
        canViewSettings={canViewSettings}
      />
    ),
    email: (
      <EmailSettings
        canEditSettings={canEditSettings}
        canViewSettings={canViewSettings}
      />
    ),
    prompts: (
      <AIPromptsSettings
        canEditSettings={canEditSettings}
        canViewSettings={canViewSettings}
      />
    ),
    coding: (
      <CodingQuestionsSettings
        canEditSettings={canEditSettings}
        canViewSettings={canViewSettings}
      />
    ),
    predefined: (
      <PredefinedQuestions
        canEditSettings={canEditSettings}
        canViewSettings={canViewSettings}
      />
    ),
    tts: (
      <VoiceProviderSettings
        canEditSettings={canEditSettings}
        canViewSettings={canViewSettings}
        canDeleteSettings={canDeleteSettings}
      />
    ),
    avatar: (
      <AvatarSettings
        canEditSettings={canEditSettings}
        canViewSettings={canViewSettings}
        canDeleteSettings={canDeleteSettings}
      />
    ),
  };

  if (!canViewSettings) {
    return (
      <div className="px-6 py-8 md:px-12 min-h-screen bg-gradient-to-br from-teal-50 to-slate-100">
        <AccessDeniedState />
      </div>
    );
  }

  return (
    <main className="system-settings">
      <div className="settings-page-heading">
        {" "}
        <div className="w-full text-left">
          <h1 className="text-3xl font-bold text-[var(--text-primary)] m-0 text-left">
            System Settings
          </h1>
          <p className="text-[var(--text-secondary)] mt-1 m-0 text-left">
            Manage your recruitment process efficiently
          </p>
        </div>
      </div>

      <nav className="settings-tabs" aria-label="System settings">
        <div className="settings-tab-list">
          {tabs.filter(tab => tab.label.toLowerCase().includes(tabSearch.trim().toLowerCase())).map(({ id, label, icon: Icon, asset }) => (
            <button
              key={id}
              onClick={() => { setActiveTab(id); setTabSearch(""); }}
              aria-current={activeTab === id ? "page" : undefined}
              className={`settings-tab ${activeTab === id ? "is-active" : ""}`}
            >
              {asset ? <img src={asset} alt="" /> : <Icon />}
              <span>{label}</span>
            </button>
          ))}
        </div>
      </nav>

      <div className={`settings-content settings-content-${activeTab}`}>{tabContent[activeTab] ?? null}</div>
    </main>
  );
};

export default Settings;
